import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Twitter, Github, Rss } from 'lucide-react';

// Computed once at module load — never changes during a session,
// so prerender and client hydration always agree on the year.
const CURRENT_YEAR = new Date().getFullYear();

const Footer = () => (
  <footer style={{
    background: 'var(--bg-secondary)',
    borderTop: '1px solid var(--border)',
    minHeight: '300px',
    contain: 'layout',
    padding: '48px 24px 32px'
  }}>
    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px', marginBottom: '40px' }}>
        <div>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', marginBottom: '16px' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <BookOpen size={16} color="white" />
            </div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>
              Wom<span style={{ color: 'var(--accent-light)' }}>lus</span>
            </span>
          </Link>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6', maxWidth: '260px' }}>
            A modern platform for writers and readers. Share your ideas with the world.
          </p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <SocialLink href="#" label="Twitter" icon={<Twitter size={16} />} />
            <SocialLink href="#" label="GitHub" icon={<Github size={16} />} />
            <SocialLink href="/blog" label="RSS Feed" icon={<Rss size={16} />} />
          </div>
        </div>

        <div>
          <h4 style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Explore</h4>
          {[
            ['Blog', '/blog'],
            ['Technology', '/category/technology'],
            ['Lifestyle', '/category/lifestyle'],
            ['Trending', '/blog?sort=views'],
            ['Featured', '/blog?featured=true'],
          ].map(([label, to]) => (
            <FooterLink key={label} to={to}>{label}</FooterLink>
          ))}
        </div>

        <div>
          <h4 style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Publish</h4>
          {[
            ['Start Writing', '/write'],
            ['Dashboard', '/dashboard'],
            ['Sign Up', '/register'],
            ['Sign In', '/login'],
          ].map(([label, to]) => (
            <FooterLink key={label} to={to}>{label}</FooterLink>
          ))}
        </div>

        <div>
          <h4 style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Company</h4>
          {[
            ['About', '#'],
            ['Privacy Policy', '#'],
            ['Terms of Service', '#'],
            ['Contact', '#'],
          ].map(([label, to]) => (
            <FooterLink key={label} to={to}>{label}</FooterLink>
          ))}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          © {CURRENT_YEAR} Womlus. All rights reserved.
        </p>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          Built with ❤️ for writers everywhere
        </p>
      </div>
    </div>
  </footer>
);

const FooterLink = ({ to, children }) => (
  <Link to={to} style={{
    display: 'block', marginBottom: '10px', fontSize: '14px',
    color: 'var(--text-secondary)', textDecoration: 'none', transition: 'color 0.2s'
  }}
    onMouseEnter={e => e.target.style.color = 'var(--accent-light)'}
    onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}>
    {children}
  </Link>
);

const SocialLink = ({ href, icon, label }) => (
  <a href={href} aria-label={label} style={{
    width: '36px', height: '36px', borderRadius: '8px',
    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--text-secondary)', transition: 'all 0.2s', textDecoration: 'none'
  }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-border)'; e.currentTarget.style.color = 'var(--accent-light)'; e.currentTarget.style.background = 'var(--accent-dim)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'var(--bg-elevated)'; }}>
    {icon}
  </a>
);

export default Footer;
